// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/SOPCenterPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActionGroup, Button, Card, Section } from "../design-system/components";
import SOPGenerator from "./SOPGenerator";
import ScreenshotFlow from "./ScreenshotFlow";
import Library from "./Library";
import SOPIntelligencePanel from "./sop/SOPIntelligencePanel";

const REQUEST_TIMEOUT_MS = 30000;

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeApiBaseUrl(value) {
  const base = String(value || "").trim();
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

function normalizeSopCenterFilter(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (
    normalized === "pending" ||
    normalized === "pending_approval" ||
    normalized === "pending approval" ||
    normalized === "review" ||
    normalized === "drafts_in_review"
  ) {
    return "pending_review";
  }

  if (
    normalized === "acknowledgment" ||
    normalized === "acknowledgments" ||
    normalized === "pending_acknowledgment" ||
    normalized === "pending_acknowledgments" ||
    normalized === "attestation" ||
    normalized === "attestations" ||
    normalized === "pending_attestation" ||
    normalized === "pending_attestations"
  ) {
    return "pending_acknowledgment";
  }

  if (
    normalized === "overdue" ||
    normalized === "overdue_sops" ||
    normalized === "outdated"
  ) {
    return "overdue";
  }

  if (
    normalized === "archived" ||
    normalized === "archive" ||
    normalized === "archived_sops" ||
    normalized === "historical" ||
    normalized === "historical_sops"
  ) {
    return "archived";
  }

  if (
    normalized === "active_findings" ||
    normalized === "findings" ||
    normalized === "linked_findings"
  ) {
    return "active_findings";
  }

  return "all";
}

function normalizePendingItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.pending_approval)) return value.pending_approval;
  if (Array.isArray(value?.pending_versions)) return value.pending_versions;
  if (Array.isArray(value?.versions)) return value.versions;
  return [];
}

function normalizeLibraryRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getPendingCount(value) {
  if (value && typeof value === "object") {
    const explicit =
      value.total ??
      value.total_count ??
      value.count ??
      value.pending_count ??
      value.pending_approval_count ??
      value.pending_versions_count;

    if (explicit !== undefined && explicit !== null) {
      return safeNumber(explicit, 0);
    }
  }

  return normalizePendingItems(value).length;
}

function getPendingCountFromLibrary(value) {
  return normalizeLibraryRows(value).reduce((total, row) => {
    const rowStatus = String(row?.status || "").trim().toLowerCase();
    const versionStatus = String(
      row?.latest_version_status || row?.version_status || ""
    )
      .trim()
      .toLowerCase();

    const rowPending =
      rowStatus === "pending approval" ||
      rowStatus === "pending_approval" ||
      versionStatus === "pending approval" ||
      versionStatus === "pending_approval";

    return total + (rowPending ? 1 : 0) + safeNumber(row?.pending_versions_count, 0);
  }, 0);
}

function mapPendingQueueItems(pendingPayload, libraryPayload) {
  const pendingItems = normalizePendingItems(pendingPayload);
  const libraryRows = normalizeLibraryRows(libraryPayload);

  if (pendingItems.length) {
    return pendingItems.map((item, index) => {
      const sopId = item.sop_id || item.sopId || item.sop?.id || item.id || "";
      const versionNumber =
        item.version_number ||
        item.latest_version_number ||
        item.pending_version_number ||
        item.version?.version_number ||
        null;

      return {
        id:
          item.version_id ||
          item.latest_version_id ||
          item.id ||
          `${sopId || "pending"}-${versionNumber || index}`,
        sopId,
        title: item.title || item.sop_title || item.sop?.title || "Untitled SOP",
        slug: item.slug || item.code || item.sop?.slug || "",
        versionNumber,
        status:
          item.status ||
          item.latest_version_status ||
          item.version_status ||
          "pending approval",
        createdAt:
          item.created_at ||
          item.latest_version_created_at ||
          item.updated_at ||
          item.version?.created_at ||
          null,
        changeSummary:
          item.change_summary ||
          item.latest_version_change_summary ||
          item.notes ||
          item.version?.change_summary ||
          "",
        sourceType:
          item.source_type ||
          item.type ||
          item.latest_version_metadata?.source_type ||
          item.latest_version_metadata?.type ||
          "pending revision",
      };
    });
  }

  return libraryRows
    .filter((row) => {
      const rowStatus = String(row?.status || "").trim().toLowerCase();
      const versionStatus = String(
        row?.latest_version_status || row?.version_status || ""
      )
        .trim()
        .toLowerCase();

      return (
        rowStatus === "pending approval" ||
        rowStatus === "pending_approval" ||
        versionStatus === "pending approval" ||
        versionStatus === "pending_approval" ||
        safeNumber(row?.pending_versions_count, 0) > 0
      );
    })
    .map((row, index) => {
      const latestMeta =
        row?.latest_version_metadata && typeof row.latest_version_metadata === "object"
          ? row.latest_version_metadata
          : {};

      return {
        id: row.latest_version_id || row.id || `library-pending-${index}`,
        sopId: row.id || row.sop_id || "",
        title: row.title || "Untitled SOP",
        slug: row.code || row.slug || "",
        versionNumber: row.latest_version_number || row.version_number || null,
        status: row.latest_version_status || row.status || "pending approval",
        createdAt: row.latest_version_created_at || row.updated_at || row.created_at || null,
        changeSummary:
          row.latest_version_change_summary ||
          latestMeta.change_summary ||
          latestMeta.notes ||
          "",
        sourceType: latestMeta.source_type || latestMeta.type || row.type || "pending revision",
      };
    });
}

function normalizePendingAcknowledgmentItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.pending)) return value.pending;
  if (Array.isArray(value?.pending_acknowledgments)) return value.pending_acknowledgments;
  if (Array.isArray(value?.pending_attestations)) return value.pending_attestations;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getPendingAcknowledgmentCount(value) {
  if (value && typeof value === "object") {
    const explicit =
      value.total ??
      value.total_count ??
      value.count ??
      value.pending_count ??
      value.pending_acknowledgment_count ??
      value.pending_attestation_count;

    if (explicit !== undefined && explicit !== null) {
      return safeNumber(explicit, 0);
    }
  }

  return normalizePendingAcknowledgmentItems(value).length;
}

function mapPendingAcknowledgmentItems(value) {
  return normalizePendingAcknowledgmentItems(value).map((item, index) => ({
    id:
      item.requirement_id ||
      item.id ||
      `${item.sop_id || item.sopId || "ack"}-${item.version_id || item.versionId || index}`,
    sopId: item.sop_id || item.sopId || "",
    versionId: item.version_id || item.versionId || "",
    requirementId: item.requirement_id || item.requirementId || item.id || "",
    title: item.title || item.sop_title || item.sop?.title || "Untitled SOP",
    slug: item.slug || item.code || item.sop?.code || "",
    versionNumber: item.version_number || item.version?.version_number || null,
    requirementType:
      item.requirement_type ||
      item.attestation_type ||
      item.type ||
      "read_and_understood",
    status: item.release_status || item.requirement_status || "effective",
    dueAt: item.due_at || item.dueAt || null,
    effectiveDate: item.effective_date || item.effectiveDate || null,
    isAttestable: item.is_attestable !== false,
    sourceType: item.source_type || item.type || "acknowledgment required",
    raw: item,
  }));
}

function formatRequirementType(value) {
  const text = String(value || "acknowledgment").replace(/_/g, " ");
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeOverdueItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.overdue)) return value.overdue;
  if (Array.isArray(value?.outdated)) return value.outdated;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getOverdueCount(value) {
  if (value && typeof value === "object") {
    const explicit =
      value.total ??
      value.total_count ??
      value.count ??
      value.overdue_count ??
      value.outdated_count;

    if (explicit !== undefined && explicit !== null) {
      return safeNumber(explicit, 0);
    }
  }

  return normalizeOverdueItems(value).length;
}

function mapOverdueQueueItems(overduePayload) {
  return normalizeOverdueItems(overduePayload).map((item, index) => ({
    id: item.id || item.sop_id || `overdue-${index}`,
    sopId: item.sop_id || item.id || "",
    title: item.title || item.sop_title || "Untitled SOP",
    slug: item.slug || item.code || "",
    status: item.status || "approved",
    createdAt: item.created_at || null,
    updatedAt: item.updated_at || null,
    detectedAt: item.outdated_detected_at || item.detected_at || null,
    daysSinceUpdate: safeNumber(
      item.days_since_update ?? item.age_days ?? item.days_overdue,
      null
    ),
    thresholdDays: safeNumber(item.outdated_days ?? item.threshold_days, null),
    reason:
      item.outdated_reason ||
      item.reason ||
      item.change_summary ||
      "This SOP is flagged as overdue/outdated.",
    category: item.category || item.framework || item.regulation_ref || "",
    sourceType: item.type || item.source_type || "outdated sop",
    raw: item,
  }));
}

function normalizeArchivedItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.archived)) return value.archived;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function mapArchivedQueueItems(archivedPayload) {
  return normalizeArchivedItems(archivedPayload).map((item, index) => {
    const metadata = item?.metadata && typeof item.metadata === "object" ? item.metadata : {};
    const latestMeta =
      item?.latest_version_metadata && typeof item.latest_version_metadata === "object"
        ? item.latest_version_metadata
        : {};

    return {
      id: item.id || item.sop_id || `archived-${index}`,
      sopId: item.sop_id || item.id || "",
      title: item.title || item.sop_title || "Untitled SOP",
      slug: item.slug || item.code || "",
      status: item.status || "archived",
      createdAt: item.created_at || item.latest_version_created_at || null,
      updatedAt: item.updated_at || null,
      archivedAt:
        item.archived_at ||
        metadata.archived_at ||
        latestMeta.archived_at ||
        item.outdated_detected_at ||
        null,
      archiveNote:
        item.archive_note ||
        metadata.archive_note ||
        latestMeta.archive_note ||
        item.notes ||
        "",
      sourceType:
        latestMeta.source_type ||
        latestMeta.type ||
        item.source_type ||
        item.type ||
        "archived sop",
      raw: item,
    };
  });
}

function formatQueueDate(value) {
  if (!value) return "—";

  const raw = String(value).trim();
  const dateOnlyMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?(?:Z|[+-]00:00)?)?$/
  );

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function SurfaceCard({ title, subtitle, right, children, style = {}, bodyStyle = {} }) {
  return (
    <Section
      title={title}
      description={subtitle}
      actions={right}
      density="default"
      divided={Boolean(title || subtitle || right)}
      className="sop-center-surface-section"
      style={style}
    >
      <div className="sop-center-surface-body" style={bodyStyle}>
        {children}
      </div>
    </Section>
  );
}

function MetricTile({ label, value, footnote, tone = "default", children }) {
  const toneColor =
    tone === "ok"
      ? "var(--color-success, #4ade80)"
      : tone === "warn"
      ? "var(--color-warning, #fbbf24)"
      : tone === "danger"
      ? "var(--color-error, #ff6b72)"
      : "var(--color-text, #e8edf7)";

  return (
    <div
      style={{
        background: "transparent",
        borderRight: "1px solid var(--oasys-color-border, rgba(148, 163, 184, 0.18))",
        padding: "16px 18px",
        minHeight: 112,
      }}
    >
      <div
        style={{
          fontSize: "var(--oasys-font-size-xs, 0.75rem)",
          color: "var(--color-text-secondary, #9aa9c2)",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "var(--oasys-font-size-2xl, 1.375rem)",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: toneColor,
          marginBottom: 6,
        }}
      >
        {value}
      </div>
      {footnote ? (
        <div
          style={{
            fontSize: "var(--oasys-font-size-sm, 0.8125rem)",
            color: "var(--color-text-secondary, #9aa9c2)",
            lineHeight: 1.45,
          }}
        >
          {footnote}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function ModePill({ active, onClick, children }) {
  return (
    <Button
      variant={active ? "primary" : "secondary"}
      size="small"
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}

function InsightCard({ title, body, active, onMouseEnter, onMouseLeave }) {
  return (
    <Card
      as="div"
      tone="secondary"
      density="compact"
      interactive
      selected={active}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="sop-center-guidance-card"
    >
      <h3 className="sop-center-guidance-card__title">{title}</h3>
      <p className="sop-center-guidance-card__body">{body}</p>
    </Card>
  );
}

function NextActionStrip({ items, onModeChange }) {
  return (
    <SurfaceCard
      title="Recommended next actions"
      subtitle="Keep SOP work moving from first draft to controlled document."
      bodyStyle={{ paddingTop: 4 }}
    >
      <div className="sop-center-next-actions">
        {items.map((line, index) => (
          <div key={line} className="sop-center-next-actions__item">
            <span className="sop-center-next-actions__number">{index + 1}.</span>
            <span>{line}</span>
          </div>
        ))}
      </div>

      {typeof onModeChange === "function" ? (
        <ActionGroup align="end" density="compact" className="sop-center-next-actions__cta">
          <Button
            variant="secondary"
            size="medium"
            className="sop-center-navigation-button"
            onClick={() => onModeChange("library")}
          >
            Open Library
          </Button>
        </ActionGroup>
      ) : null}
    </SurfaceCard>
  );
}

function DocumentControlInbox({
  pendingQueue,
  overdueQueue,
  acknowledgmentQueue,
  onOpenQueue,
}) {
  const pendingCount = safeNumber(pendingQueue?.count, 0);
  const overdueCount = safeNumber(overdueQueue?.count, 0);
  const acknowledgmentCount = safeNumber(acknowledgmentQueue?.count, 0);

  const compactAction = (label, value, tone, queueType) => {
    const toneColor =
      tone === "danger"
        ? "var(--color-error, #ff6b72)"
        : tone === "warn"
        ? "var(--color-warning, #f7c75b)"
        : tone === "ok"
        ? "var(--color-success, #45d69c)"
        : "var(--color-text, #e8edf7)";

    return (
      <button
        type="button"
        onClick={() => onOpenQueue(queueType)}
        style={{
          minWidth: 0,
          padding: "7px 8px",
          border: "1px solid var(--oasys-color-border, rgba(148, 163, 184, 0.18))",
          borderRadius: "var(--oasys-radius-compact, 6px)",
          background: "transparent",
          color: "var(--color-text, #e8edf7)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ fontSize: 11, color: "var(--color-text-secondary, #9aa9c2)" }}>
          {label}
        </div>
        <div style={{ marginTop: 2, fontSize: 16, fontWeight: 700, color: toneColor }}>
          {value}
        </div>
      </button>
    );
  };

  return (
    <div
      style={{
        background: "transparent",
        border: "none",
        borderRadius: 0,
        padding: "14px 16px",
        minHeight: 112,
        display: "grid",
        alignContent: "start",
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: "var(--oasys-font-size-xs, 0.75rem)",
          color: "var(--color-text-secondary, #9aa9c2)",
          fontWeight: 600,
        }}
      >
        Document Control
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
        {compactAction(
          "Overdue",
          overdueQueue?.loading ? "…" : overdueCount,
          overdueCount > 0 ? "danger" : "default",
          "overdue"
        )}
        {compactAction(
          "Review",
          pendingQueue?.loading ? "…" : pendingCount,
          pendingCount > 0 ? "warn" : "default",
          "pending_review"
        )}
        {compactAction(
          "Acks",
          acknowledgmentQueue?.loading ? "…" : acknowledgmentCount,
          acknowledgmentCount > 0 ? "ok" : "default",
          "pending_acknowledgment"
        )}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => onOpenQueue("archived")}
          style={{
            border: 0,
            padding: 0,
            background: "transparent",
            color: "var(--color-text-secondary, #9aa9c2)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Archived SOPs
        </button>
        <button
          type="button"
          onClick={() => onOpenQueue("active_findings")}
          style={{
            border: 0,
            padding: 0,
            background: "transparent",
            color: "var(--color-text-secondary, #9aa9c2)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Finding-linked SOPs
        </button>
      </div>
    </div>
  );
}

function DocumentControlModal({
  queueType,
  onClose,
  pendingQueue,
  overdueQueue,
  archivedQueue,
  acknowledgmentQueue,
  onOpenLibraryQueue,
}) {
  if (!queueType) return null;

  const isPending = queueType === "pending_review";
  const isOverdue = queueType === "overdue";
  const isArchived = queueType === "archived";
  const isAcknowledgment = queueType === "pending_acknowledgment";
  const isFindings = queueType === "active_findings";

  const title = isPending
    ? "Drafts in Review"
    : isOverdue
    ? "Overdue SOPs"
    : isArchived
    ? "Archived SOPs"
    : isAcknowledgment
    ? "Pending Acknowledgments"
    : "SOPs Tied to Active Findings";

  const subtitle = isPending
    ? "SOP revisions awaiting reviewer action before they become controlled operating documents."
    : isOverdue
    ? "Approved SOPs flagged as overdue/outdated by the document-control review policy."
    : isArchived
    ? "Controlled documents removed from active execution but retained for audit history."
    : isAcknowledgment
    ? "Effective SOPs that require your read-and-understood acknowledgment before the requirement is complete."
    : "This queue will activate when SOPs are mapped to controls, findings, and remediation workflows.";

  const pendingItems = Array.isArray(pendingQueue?.items) ? pendingQueue.items : [];
  const overdueItems = Array.isArray(overdueQueue?.items) ? overdueQueue.items : [];
  const archivedItems = Array.isArray(archivedQueue?.items) ? archivedQueue.items : [];
  const acknowledgmentItems = Array.isArray(acknowledgmentQueue?.items)
    ? acknowledgmentQueue.items
    : [];
  const items = isOverdue
    ? overdueItems
    : isArchived
    ? archivedItems
    : isAcknowledgment
    ? acknowledgmentItems
    : pendingItems;
  const pendingCount = safeNumber(pendingQueue?.count, pendingItems.length);
  const overdueCount = safeNumber(overdueQueue?.count, overdueItems.length);
  const archivedCount = safeNumber(archivedQueue?.count, archivedItems.length);
  const acknowledgmentCount = safeNumber(
    acknowledgmentQueue?.count,
    acknowledgmentItems.length
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        background: "rgba(15, 23, 42, 0.38)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "28px",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(920px, 100%)",
          maxHeight: "min(760px, 92vh)",
          overflow: "auto",
          background: "var(--color-surface, #14213a)",
          color: "var(--color-text, #e8edf7)",
          borderRadius: 28,
          border:
            "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 88%)",
          boxShadow: "0 28px 80px rgba(15, 23, 42, 0.28)",
        }}
      >
        <div
          style={{
            padding: "22px 24px 18px",
            borderBottom:
              "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 90%)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
                fontWeight: 800,
                color: "var(--color-primary, #0C8F5D)",
                marginBottom: 8,
              }}
            >
              Document Control Queue
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 28,
                lineHeight: 1.1,
                letterSpacing: "-0.04em",
              }}
            >
              {title}
            </h2>
            <p
              style={{
                margin: "10px 0 0",
                color: "var(--color-text-secondary, #9aa9c2)",
                lineHeight: 1.6,
                maxWidth: 720,
              }}
            >
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close document-control queue"
            style={{
              border: "none",
              background: "transparent",
              color: "var(--color-text-secondary, #9aa9c2)",
              fontSize: 26,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {isAcknowledgment ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <MetricTile
                  label="Pending acknowledgments"
                  value={acknowledgmentQueue?.loading ? "…" : acknowledgmentCount}
                  footnote="Effective SOP versions awaiting your acknowledgment"
                  tone={acknowledgmentCount > 0 ? "warn" : "ok"}
                />
                <MetricTile
                  label="Action required"
                  value={acknowledgmentCount > 0 ? "Acknowledge" : "No"}
                  footnote="Open each SOP and record read-and-understood"
                  tone={acknowledgmentCount > 0 ? "warn" : "ok"}
                />
                <MetricTile
                  label="Release state"
                  value="Effective"
                  footnote="Only attestable SOP versions appear here"
                  tone="ok"
                />
              </div>

              {acknowledgmentQueue?.error ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 16,
                    background:
                      "color-mix(in srgb, var(--color-error, #b91c1c), transparent 92%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 78%)",
                    color: "var(--color-error, #b91c1c)",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  {acknowledgmentQueue.error}
                </div>
              ) : null}

              {acknowledgmentQueue?.loading ? (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                    color: "var(--color-text-secondary, #9aa9c2)",
                  }}
                >
                  Loading pending acknowledgments…
                </div>
              ) : acknowledgmentItems.length ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {acknowledgmentItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        border:
                          "1px solid color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 72%)",
                        borderRadius: 18,
                        padding: "15px 16px",
                        background:
                          "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 95%)",
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 16,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <strong style={{ fontSize: 17 }}>{item.title}</strong>
                          {item.versionNumber ? (
                            <span
                              style={{
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "var(--color-primary, #0C8F5D)",
                                background: "rgba(12, 143, 93, 0.08)",
                                border: "1px solid rgba(12, 143, 93, 0.18)",
                              }}
                            >
                              v{item.versionNumber}
                            </span>
                          ) : null}
                          <span
                            style={{
                              borderRadius: 999,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "var(--color-primary, #0C8F5D)",
                              background: "rgba(12, 143, 93, 0.08)",
                              border: "1px solid rgba(12, 143, 93, 0.18)",
                              textTransform: "uppercase",
                            }}
                          >
                            Acknowledgment Required
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            color: "var(--color-text-secondary, #9aa9c2)",
                            fontSize: 13,
                            lineHeight: 1.45,
                          }}
                        >
                          <span>{item.slug || "No code"}</span>
                          <span>{formatRequirementType(item.requirementType)}</span>
                          <span>Effective {formatQueueDate(item.effectiveDate)}</span>
                          <span>Due {formatQueueDate(item.dueAt)}</span>
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            color: "var(--color-text-secondary, #9aa9c2)",
                            fontSize: 14,
                            lineHeight: 1.55,
                          }}
                        >
                          Open the SOP, review the current controlled version, and record your acknowledgment from the Release & acknowledgments card.
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => onOpenLibraryQueue("pending_acknowledgment")}
                        style={{
                          border:
                            "1px solid color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 72%)",
                          background:
                            "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 92%)",
                          color: "var(--color-primary, #0C8F5D)",
                          borderRadius: 999,
                          padding: "10px 14px",
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Open SOP
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-success, #15803d), transparent 93%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-success, #15803d), transparent 76%)",
                    color: "var(--color-text, #e8edf7)",
                    lineHeight: 1.6,
                  }}
                >
                  No SOP acknowledgments are pending for you right now.
                </div>
              )}
            </>
          ) : isPending ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <MetricTile
                  label="Awaiting review"
                  value={pendingQueue?.loading ? "…" : pendingCount}
                  footnote="Pending SOP revision candidates"
                  tone={pendingCount > 0 ? "warn" : "ok"}
                />
                <MetricTile
                  label="Action required"
                  value={pendingCount > 0 ? "Yes" : "No"}
                  footnote="Approve or reject each candidate"
                  tone={pendingCount > 0 ? "warn" : "ok"}
                />
                <MetricTile
                  label="Controlled version"
                  value="Protected"
                  footnote="Current SOP remains active until approval"
                  tone="ok"
                />
              </div>

              {pendingQueue?.error ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 16,
                    background:
                      "color-mix(in srgb, var(--color-error, #b91c1c), transparent 92%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 78%)",
                    color: "var(--color-error, #b91c1c)",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  {pendingQueue.error}
                </div>
              ) : null}

              {pendingQueue?.loading ? (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                    color: "var(--color-text-secondary, #9aa9c2)",
                  }}
                >
                  Loading drafts in review…
                </div>
              ) : items.length ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        border:
                          "1px solid color-mix(in srgb, var(--color-warning, #f59e0b), transparent 74%)",
                        borderRadius: 18,
                        padding: "15px 16px",
                        background:
                          "color-mix(in srgb, var(--color-warning, #f59e0b), transparent 94%)",
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 16,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <strong style={{ fontSize: 17 }}>{item.title}</strong>
                          {item.versionNumber ? (
                            <span
                              style={{
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#b45309",
                                background: "rgba(180, 83, 9, 0.08)",
                                border: "1px solid rgba(180, 83, 9, 0.18)",
                              }}
                            >
                              v{item.versionNumber}
                            </span>
                          ) : null}
                          <span
                            style={{
                              borderRadius: 999,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "#b45309",
                              background: "rgba(180, 83, 9, 0.08)",
                              border: "1px solid rgba(180, 83, 9, 0.18)",
                              textTransform: "uppercase",
                            }}
                          >
                            {item.status || "Pending Approval"}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            color: "var(--color-text-secondary, #9aa9c2)",
                            fontSize: 13,
                            lineHeight: 1.45,
                          }}
                        >
                          <span>{item.slug || "No code"}</span>
                          <span>Created {formatQueueDate(item.createdAt)}</span>
                          <span>{item.sourceType}</span>
                        </div>

                        {item.changeSummary ? (
                          <div
                            style={{
                              marginTop: 8,
                              color: "var(--color-text-secondary, #9aa9c2)",
                              fontSize: 14,
                              lineHeight: 1.55,
                            }}
                          >
                            {item.changeSummary}
                          </div>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => onOpenLibraryQueue("pending_review")}
                        style={{
                          border:
                            "1px solid color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 72%)",
                          background:
                            "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 92%)",
                          color: "var(--color-primary, #0C8F5D)",
                          borderRadius: 999,
                          padding: "10px 14px",
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Open Review
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-success, #15803d), transparent 93%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-success, #15803d), transparent 76%)",
                    color: "var(--color-text, #e8edf7)",
                    lineHeight: 1.6,
                  }}
                >
                  No SOP revisions are waiting for approval right now.
                </div>
              )}
            </>
          ) : isOverdue ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <MetricTile
                  label="Overdue SOPs"
                  value={overdueQueue?.loading ? "…" : overdueCount}
                  footnote="Approved SOPs flagged by outdated detection"
                  tone={overdueCount > 0 ? "danger" : "ok"}
                />
                <MetricTile
                  label="Action required"
                  value={overdueCount > 0 ? "Review" : "No"}
                  footnote="Refresh, revise, or regenerate outdated SOPs"
                  tone={overdueCount > 0 ? "danger" : "ok"}
                />
                <MetricTile
                  label="Policy"
                  value="Live"
                  footnote="Uses the backend outdated SOP detector"
                  tone="ok"
                />
              </div>

              {overdueQueue?.error ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 16,
                    background:
                      "color-mix(in srgb, var(--color-error, #b91c1c), transparent 92%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 78%)",
                    color: "var(--color-error, #b91c1c)",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  {overdueQueue.error}
                </div>
              ) : null}

              {overdueQueue?.loading ? (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                    color: "var(--color-text-secondary, #9aa9c2)",
                  }}
                >
                  Loading overdue SOPs…
                </div>
              ) : overdueItems.length ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {overdueItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        border:
                          "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 74%)",
                        borderRadius: 18,
                        padding: "15px 16px",
                        background:
                          "color-mix(in srgb, var(--color-error, #b91c1c), transparent 95%)",
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 16,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <strong style={{ fontSize: 17 }}>{item.title}</strong>
                          {item.daysSinceUpdate !== null ? (
                            <span
                              style={{
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "var(--color-error, #b91c1c)",
                                background: "rgba(185, 28, 28, 0.08)",
                                border: "1px solid rgba(185, 28, 28, 0.18)",
                              }}
                            >
                              {item.daysSinceUpdate} days old
                            </span>
                          ) : null}
                          <span
                            style={{
                              borderRadius: 999,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "var(--color-error, #b91c1c)",
                              background: "rgba(185, 28, 28, 0.08)",
                              border: "1px solid rgba(185, 28, 28, 0.18)",
                              textTransform: "uppercase",
                            }}
                          >
                            Outdated
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            color: "var(--color-text-secondary, #9aa9c2)",
                            fontSize: 13,
                            lineHeight: 1.45,
                          }}
                        >
                          <span>{item.slug || "No code"}</span>
                          <span>Status {item.status || "approved"}</span>
                          <span>Updated {formatQueueDate(item.updatedAt)}</span>
                          {item.thresholdDays !== null ? (
                            <span>Threshold {item.thresholdDays} days</span>
                          ) : null}
                        </div>

                        {item.reason ? (
                          <div
                            style={{
                              marginTop: 8,
                              color: "var(--color-text-secondary, #9aa9c2)",
                              fontSize: 14,
                              lineHeight: 1.55,
                            }}
                          >
                            {item.reason}
                          </div>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => onOpenLibraryQueue("overdue")}
                        style={{
                          border:
                            "1px solid color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 72%)",
                          background:
                            "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 92%)",
                          color: "var(--color-primary, #0C8F5D)",
                          borderRadius: 999,
                          padding: "10px 14px",
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Open SOP
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-success, #15803d), transparent 93%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-success, #15803d), transparent 76%)",
                    color: "var(--color-text, #e8edf7)",
                    lineHeight: 1.6,
                  }}
                >
                  No overdue SOPs found for this tenant.
                </div>
              )}
            </>
          ) : isArchived ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <MetricTile
                  label="Archive status"
                  value="Retained"
                  footnote="Archived SOPs are preserved for audit history"
                  tone="default"
                />
                <MetricTile
                  label="Action required"
                  value="No"
                  footnote="Archive is historical, not an urgent queue"
                  tone="ok"
                />
                <MetricTile
                  label="Archived records"
                  value={archivedQueue?.loading ? "…" : archivedCount}
                  footnote="Shown here for verification only"
                  tone="default"
                />
              </div>

              {archivedQueue?.error ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 16,
                    background:
                      "color-mix(in srgb, var(--color-error, #b91c1c), transparent 92%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 78%)",
                    color: "var(--color-error, #b91c1c)",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  {archivedQueue.error}
                </div>
              ) : null}

              {archivedQueue?.loading ? (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                    color: "var(--color-text-secondary, #9aa9c2)",
                  }}
                >
                  Loading archived SOPs…
                </div>
              ) : archivedItems.length ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {archivedItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        border:
                          "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 86%)",
                        borderRadius: 18,
                        padding: "15px 16px",
                        background:
                          "color-mix(in srgb, var(--color-text, #e8edf7), transparent 97%)",
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr)",
                        gap: 16,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <strong style={{ fontSize: 17 }}>{item.title}</strong>
                          <span
                            style={{
                              borderRadius: 999,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "var(--color-text-secondary, #9aa9c2)",
                              background: "rgba(100, 116, 139, 0.08)",
                              border: "1px solid rgba(100, 116, 139, 0.18)",
                              textTransform: "uppercase",
                            }}
                          >
                            Archived
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            color: "var(--color-text-secondary, #9aa9c2)",
                            fontSize: 13,
                            lineHeight: 1.45,
                          }}
                        >
                          <span>{item.slug || "No code"}</span>
                          <span>Created {formatQueueDate(item.createdAt)}</span>
                          <span>Archived {formatQueueDate(item.archivedAt)}</span>
                          <span>{item.sourceType}</span>
                        </div>

                        {item.archiveNote ? (
                          <div
                            style={{
                              marginTop: 8,
                              color: "var(--color-text-secondary, #9aa9c2)",
                              fontSize: 14,
                              lineHeight: 1.55,
                            }}
                          >
                            {item.archiveNote}
                          </div>
                        ) : null}
                      </div>

                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: 18,
                    background:
                      "color-mix(in srgb, var(--color-text, #e8edf7), transparent 97%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 88%)",
                    color: "var(--color-text, #e8edf7)",
                    lineHeight: 1.6,
                  }}
                >
                  No archived SOPs found for this tenant.
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                padding: "18px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 88%)",
                color: "var(--color-text-secondary, #9aa9c2)",
                lineHeight: 1.65,
              }}
            >
              {isFindings
                ? "SOPs tied to active findings will appear here once SOP-to-control and SOP-to-finding relationships are added."
                : "This document-control queue is not available yet."}
            </div>
          )}

          <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                border:
                  "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 84%)",
                background: "transparent",
                color: "var(--color-text, #e8edf7)",
                borderRadius: 999,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SOPCenterPage({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
  onModeChange,
  modeParams = {},
  routeParams = {},
  initialFilter = null,
}) {
  const incomingFilter =
    modeParams?.filter ||
    modeParams?.sopFilter ||
    routeParams?.filter ||
    initialFilter ||
    null;

  const targetSopId = String(
    modeParams?.sopId ||
      modeParams?.sop_id ||
      routeParams?.sopId ||
      routeParams?.sop_id ||
      ""
  ).trim();

  const [builderMode, setBuilderMode] = useState("guided");
  const [hoveredGuidanceIndex, setHoveredGuidanceIndex] = useState(null);
  const [libraryQueueView, setLibraryQueueView] = useState("all");
  const [documentControlModal, setDocumentControlModal] = useState(null);
  const [pendingQueue, setPendingQueue] = useState({
    count: 0,
    items: [],
    loaded: false,
    loading: false,
    error: "",
  });
  const [overdueQueue, setOverdueQueue] = useState({
    count: 0,
    items: [],
    loaded: false,
    loading: false,
    error: "",
  });
  const [archivedQueue, setArchivedQueue] = useState({
    count: 0,
    items: [],
    loaded: false,
    loading: false,
    error: "",
  });
  const [acknowledgmentQueue, setAcknowledgmentQueue] = useState({
    count: 0,
    items: [],
    loaded: false,
    loading: false,
    error: "",
  });

  const normalizedBase = useMemo(() => normalizeApiBaseUrl(apiBaseUrl), [apiBaseUrl]);

  const tenantId =
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const userId =
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.sub ||
    "";

  const fetchQueueToken = useCallback(async () => {
    if (typeof getAccessToken === "function") {
      return await getAccessToken();
    }
    return "";
  }, [getAccessToken]);

  const fetchJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      if (!normalizedBase) {
        throw new Error("SOP Center requires apiBaseUrl.");
      }

      const searchParams = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  const requestJson = useCallback(
    async (path, options = {}) => {
      if (!normalizedBase) {
        throw new Error("SOP Center requires apiBaseUrl.");
      }

      const token = await fetchQueueToken();
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("sop-intelligence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const headers = {
          "Content-Type": "application/json",
          ...(options.headers || {}),
        };

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantId) headers["X-Tenant-Id"] = tenantId;
        if (userId) headers["X-User-Id"] = userId;

        const response = await fetch(`${normalizedBase}${path}`, {
          method: options.method || "GET",
          headers,
          body:
            options.body !== undefined
              ? JSON.stringify(options.body)
              : undefined,
          signal: controller.signal,
        });

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(`${response.status} ${response.statusText}: ${text}`);
        }

        return response.json();
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    [fetchQueueToken, normalizedBase, tenantId, userId]
  );

  const loadPendingQueue = useCallback(
    async (signal) => {
      setPendingQueue((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchQueueToken();

        const [pendingResult, libraryResult] = await Promise.allSettled([
          fetchJsonWithToken(token, "/sops/pending-approval", { limit: 250 }, signal),
          fetchJsonWithToken(
            token,
            "/sops/library",
            { limit: 250, tenant_id: tenantId },
            signal
          ),
        ]);

        const pendingPayload =
          pendingResult.status === "fulfilled" ? pendingResult.value : null;
        const libraryPayload =
          libraryResult.status === "fulfilled" ? libraryResult.value : null;

        if (pendingResult.status === "rejected" && libraryResult.status === "rejected") {
          throw pendingResult.reason || libraryResult.reason;
        }

        const pendingCount = getPendingCount(pendingPayload);
        const fallbackCount = getPendingCountFromLibrary(libraryPayload);
        const items = mapPendingQueueItems(pendingPayload, libraryPayload);

        setPendingQueue({
          count: Math.max(pendingCount, fallbackCount, items.length),
          items,
          loaded: true,
          loading: false,
          error: "",
        });
      } catch (error) {
        console.error("Failed to load SOP pending review queue", error);
        setPendingQueue({
          count: 0,
          items: [],
          loaded: false,
          loading: false,
          error:
            error?.message ||
            "Unable to load the SOP review queue right now.",
        });
      }
    },
    [fetchJsonWithToken, fetchQueueToken, tenantId]
  );

  const loadPendingAcknowledgmentQueue = useCallback(
    async (signal) => {
      setAcknowledgmentQueue((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchQueueToken();
        const acknowledgmentPayload = await fetchJsonWithToken(
          token,
          "/sops/attestations/pending",
          { limit: 250, tenant_id: tenantId },
          signal
        );

        const items = mapPendingAcknowledgmentItems(acknowledgmentPayload);

        setAcknowledgmentQueue({
          count: Math.max(getPendingAcknowledgmentCount(acknowledgmentPayload), items.length),
          items,
          loaded: true,
          loading: false,
          error: "",
        });
      } catch (error) {
        console.error("Failed to load SOP pending acknowledgment queue", error);
        setAcknowledgmentQueue({
          count: 0,
          items: [],
          loaded: false,
          loading: false,
          error:
            error?.message ||
            "Unable to load pending SOP acknowledgments right now.",
        });
      }
    },
    [fetchJsonWithToken, fetchQueueToken, tenantId]
  );

  const loadOverdueQueue = useCallback(
    async (signal) => {
      setOverdueQueue((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchQueueToken();
        const overduePayload = await fetchJsonWithToken(
          token,
          "/sops/overdue",
          { refresh: true, limit: 250 },
          signal
        );

        const items = mapOverdueQueueItems(overduePayload);

        setOverdueQueue({
          count: Math.max(getOverdueCount(overduePayload), items.length),
          items,
          loaded: true,
          loading: false,
          error: "",
        });
      } catch (error) {
        console.error("Failed to load SOP overdue queue", error);
        setOverdueQueue({
          count: 0,
          items: [],
          loaded: false,
          loading: false,
          error:
            error?.message ||
            "Unable to load the overdue SOP queue right now.",
        });
      }
    },
    [fetchJsonWithToken, fetchQueueToken]
  );

  const loadArchivedQueue = useCallback(
    async (signal) => {
      setArchivedQueue((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchQueueToken();
        const archivedPayload = await fetchJsonWithToken(
          token,
          "/sops/library",
          { is_archived: true, limit: 250, tenant_id: tenantId },
          signal
        );

        const items = mapArchivedQueueItems(archivedPayload);

        setArchivedQueue({
          count: items.length,
          items,
          loaded: true,
          loading: false,
          error: "",
        });
      } catch (error) {
        console.error("Failed to load SOP archive queue", error);
        setArchivedQueue({
          count: 0,
          items: [],
          loaded: false,
          loading: false,
          error:
            error?.message ||
            "Unable to load archived SOPs right now.",
        });
      }
    },
    [fetchJsonWithToken, fetchQueueToken, tenantId]
  );

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("sop-center-queue-timeout"),
      REQUEST_TIMEOUT_MS
    );

    loadPendingQueue(controller.signal);
    loadPendingAcknowledgmentQueue(controller.signal);
    loadOverdueQueue(controller.signal);
    loadArchivedQueue(controller.signal);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [loadArchivedQueue, loadOverdueQueue, loadPendingAcknowledgmentQueue, loadPendingQueue]);

  const refreshDocumentControlQueue = useCallback(
    async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("sop-center-queue-refresh-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        await Promise.allSettled([
          loadPendingQueue(controller.signal),
          loadPendingAcknowledgmentQueue(controller.signal),
          loadOverdueQueue(controller.signal),
          loadArchivedQueue(controller.signal),
        ]);
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    [loadArchivedQueue, loadOverdueQueue, loadPendingAcknowledgmentQueue, loadPendingQueue]
  );

  useEffect(() => {
    const handleDocumentControlChanged = () => {
      refreshDocumentControlQueue();
    };

    window.addEventListener(
      "oasys:sop-document-control-changed",
      handleDocumentControlChanged
    );

    return () => {
      window.removeEventListener(
        "oasys:sop-document-control-changed",
        handleDocumentControlChanged
      );
    };
  }, [refreshDocumentControlQueue]);

  useEffect(() => {
    const normalized = normalizeSopCenterFilter(incomingFilter);
    if (normalized !== "all") {
      setDocumentControlModal(normalized);
      setLibraryQueueView(
        normalized === "pending_review" ||
        normalized === "pending_acknowledgment" ||
        normalized === "overdue" ||
        normalized === "archived"
          ? normalized
          : "all"
      );
      refreshDocumentControlQueue();
    }
  }, [incomingFilter, refreshDocumentControlQueue]);

  useEffect(() => {
    if (!targetSopId) return;

    setDocumentControlModal(null);
    setLibraryQueueView("all");

    window.setTimeout(() => {
      const libraryEl = document.getElementById("sop-library-workspace");
      if (libraryEl) {
        const top = libraryEl.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: "smooth",
        });
      }
    }, 120);
  }, [targetSopId]);

  const openDocumentControlQueue = useCallback((queueType) => {
    const normalized = normalizeSopCenterFilter(queueType);
    setDocumentControlModal(normalized === "all" ? "pending_review" : normalized);

    if (
      normalized === "pending_review" ||
      normalized === "pending_acknowledgment" ||
      normalized === "overdue" ||
      normalized === "archived"
    ) {
      refreshDocumentControlQueue();
    }
  }, [refreshDocumentControlQueue]);

  const closeDocumentControlQueue = useCallback(() => {
    setDocumentControlModal(null);
  }, []);

  const openLibraryQueue = useCallback((queueType) => {
    const normalized = normalizeSopCenterFilter(queueType);
    const nextQueueView = normalized === "all" ? "pending_review" : normalized;

    setDocumentControlModal(null);
    setLibraryQueueView(nextQueueView);
    refreshDocumentControlQueue();

    window.setTimeout(() => {
      setLibraryQueueView(nextQueueView);

      const libraryEl = document.getElementById("sop-library-workspace");
      if (libraryEl) {
        libraryEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
  }, [refreshDocumentControlQueue]);

  const builderTitle =
    builderMode === "guided" ? "Guided SOP Builder" : "Screenshot SOP Builder";

  const builderSubtitle =
    builderMode === "guided"
      ? "Create a structured SOP from objective, process name, steps, and compliance requirements."
      : "Upload screenshots or workflow captures and let OASYS infer the missing procedural detail.";

  const operatorCards =
    builderMode === "guided"
      ? [
          {
            title: "Best for structured process design",
            body:
              "Use the guided builder when you already know the process, need cleaner wording, or want to draft a formal SOP from scratch.",
          },
          {
            title: "Output expectation",
            body:
              "This flow is best for controlled internal procedures, onboarding instructions, repeatable operating steps, and early SOP standardization.",
          },
          {
            title: "Operator tip",
            body:
              "Keep the objective tight, use action-oriented step language, and treat compliance notes as control points instead of general comments.",
          },
        ]
      : [
          {
            title: "Best for captured workflows",
            body:
              "Use the screenshot builder when the workflow already exists in software and you want OASYS to convert visual process evidence into a usable SOP draft.",
          },
          {
            title: "Output expectation",
            body:
              "This flow is best for tool-based operations, admin tasks, customer service workflows, and any process where screenshots tell the real story.",
          },
          {
            title: "Operator tip",
            body:
              "Upload screenshots in order, remove duplicates, and use notes only for context the images cannot explain on their own.",
          },
        ];

  const nextActionItems = [
    "Draft the SOP in the builder that best matches the workflow.",
    "Review pending SOP revisions and acknowledge effective SOPs assigned to you.",
    "Use the library below to keep high-value SOPs organized as your process inventory grows.",
  ];

  const libraryTitle =
    libraryQueueView === "pending_review"
      ? "Pending Review Queue"
      : libraryQueueView === "pending_acknowledgment"
      ? "Pending Acknowledgments"
      : libraryQueueView === "overdue"
      ? "Overdue SOP Queue"
      : libraryQueueView === "archived"
      ? "Archived SOPs"
      : libraryQueueView === "active_findings"
      ? "SOPs Tied to Active Findings"
      : "Library & Templates";

  const librarySubtitle =
    libraryQueueView === "pending_review"
      ? "Review SOP revisions awaiting approval. Open a pending SOP, inspect its version history, then approve or reject from the SOP detail modal."
      : libraryQueueView === "pending_acknowledgment"
      ? "Review effective SOPs that still require your acknowledgment. Open a SOP, review the controlled version, then record read-and-understood from the detail modal."
      : libraryQueueView === "overdue"
      ? "Review SOPs flagged as overdue/outdated by the backend document-control detector."
      : libraryQueueView === "archived"
      ? "Review SOPs removed from active execution but retained for audit history."
      : libraryQueueView === "active_findings"
      ? "Finding-linked SOP filtering will be wired after SOP-to-control/finding relationships are added."
      : "Review SOP inventory, browse existing records, and use template scaffolds to accelerate future SOP creation.";

  const libraryStatusFilter =
    libraryQueueView === "pending_review"
      ? "pending_approval"
      : libraryQueueView === "pending_acknowledgment"
      ? "pending_acknowledgment"
      : libraryQueueView === "overdue"
      ? "overdue"
      : libraryQueueView === "archived"
      ? "archived"
      : "all";

  return (
    <div
      className="sop-center-page oasys-page-canvas"
      style={{
        "--color-text": "var(--oasys-color-text-primary)",
        "--color-text-secondary": "var(--oasys-color-text-secondary)",
        "--color-text-muted": "var(--oasys-color-text-muted)",
        "--color-surface": "var(--oasys-color-surface-primary)",
        "--color-primary": "var(--oasys-color-brand)",
        "--color-primary-strong": "var(--oasys-color-brand-hover)",
        "--color-on-primary": "var(--oasys-color-text-inverse)",
        "--color-success": "var(--oasys-color-success)",
        "--color-warning": "var(--oasys-color-warning)",
        "--color-error": "var(--oasys-color-danger)",
      }}
    >
      <div className="oasys-page-shell sop-center-page__shell">
        <style>{`
          .sop-center-page__metadata {
            margin-top: var(--oasys-space-2);
            color: var(--oasys-color-text-muted);
            font-size: var(--oasys-font-size-xs);
            line-height: var(--oasys-line-height-body);
          }

          .sop-center-overview-card {
            margin-bottom: var(--oasys-space-5);
            overflow: hidden;
          }

          .sop-center-overview-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .sop-center-surface-section .oasys-section__body {
            padding: var(--oasys-space-5);
          }

          .sop-center-surface-body {
            min-width: 0;
          }

          .sop-center-next-actions {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            border: 1px solid var(--oasys-color-border);
            border-radius: var(--oasys-radius-control);
            overflow: hidden;
          }

          .sop-center-next-actions__item {
            min-height: 56px;
            display: flex;
            align-items: flex-start;
            gap: var(--oasys-space-2);
            padding: 11px 14px;
            color: var(--oasys-color-text-primary);
            font-size: var(--oasys-font-size-md);
            font-weight: var(--oasys-font-weight-semibold);
            line-height: var(--oasys-line-height-body);
          }

          .sop-center-next-actions__item + .sop-center-next-actions__item {
            border-left: 1px solid var(--oasys-color-divider);
          }

          .sop-center-next-actions__number {
            color: var(--oasys-color-text-secondary);
          }

          .sop-center-next-actions__cta {
            margin-top: var(--oasys-space-3);
          }

          .sop-center-navigation-button {
            min-width: 156px;
          }

          .oasys-theme-dark .sop-center-navigation-button,
          :root[data-theme="dark"] .sop-center-navigation-button {
            color: #172033;
            background: #f7f9fc;
            border-color: rgba(234, 240, 255, 0.22);
          }

          .oasys-theme-dark .sop-center-navigation-button:hover:not(:disabled),
          :root[data-theme="dark"] .sop-center-navigation-button:hover:not(:disabled) {
            background: #ffffff;
            border-color: rgba(234, 240, 255, 0.36);
          }

          .sop-center-builder-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.75fr);
            gap: var(--oasys-space-5);
            align-items: start;
            margin-bottom: var(--oasys-space-5);
          }

          .sop-center-guidance-card {
            padding: var(--oasys-space-4);
          }

          .sop-center-guidance-card__title {
            margin: 0 0 var(--oasys-space-2);
            font-size: var(--oasys-font-size-base);
          }

          .sop-center-guidance-card__body {
            margin: 0;
            color: var(--oasys-color-text-secondary);
            font-size: var(--oasys-font-size-md);
            line-height: var(--oasys-line-height-body);
          }

          @media (max-width: 980px) {
            .sop-center-overview-grid,
            .sop-center-next-actions,
            .sop-center-builder-grid {
              grid-template-columns: 1fr;
            }

            .sop-center-next-actions__item + .sop-center-next-actions__item {
              border-left: 0;
              border-top: 1px solid var(--oasys-color-divider);
            }
          }
        `}</style>
        <header className="oasys-page-header sop-center-page__header">
          <div className="oasys-page-header__content">
            <h1 className="oasys-page-header__title">SOP Center</h1>
            <p className="oasys-page-header__description">
              Create, govern, review, and maintain controlled operating procedures from one operational workspace.
            </p>
            <div className="sop-center-page__metadata">
              Drafting: Guided + Screenshot · Outputs: PDF + Word
            </div>
          </div>
        </header>

        <SOPIntelligencePanel
          requestJson={requestJson}
        />

      <Card
        tone="primary"
        density="compact"
        className="sop-center-overview-card"
      >
        <div className="sop-center-overview-grid">
        <MetricTile
          label="Guided Builder"
          value="Live"
          footnote="Structured SOP drafting"
          tone="ok"
        />
        <MetricTile
          label="Screenshot Builder"
          value="Live"
          footnote="Process capture to SOP"
          tone="ok"
        />
        <MetricTile
          label="Library"
          value="Active"
          footnote="Controlled SOP inventory"
          tone="ok"
        />
        <DocumentControlInbox
          pendingQueue={pendingQueue}
          overdueQueue={overdueQueue}
          acknowledgmentQueue={acknowledgmentQueue}
          onOpenQueue={openDocumentControlQueue}
        />
        </div>
      </Card>

      <div style={{ marginBottom: 18 }}>
        <NextActionStrip items={nextActionItems} onModeChange={onModeChange} />
      </div>

      <div className="sop-center-builder-grid">
        <div style={{ minWidth: 0 }}>
          <SurfaceCard
            title="Builder Workspace"
            subtitle="Choose the SOP creation path that best fits the workflow you are documenting."
            right={
              <ActionGroup align="end" density="compact">
                <ModePill
                  active={builderMode === "guided"}
                  onClick={() => {
                    setBuilderMode("guided");
                    setHoveredGuidanceIndex(null);
                  }}
                >
                  Guided Builder
                </ModePill>
                <ModePill
                  active={builderMode === "screenshots"}
                  onClick={() => {
                    setBuilderMode("screenshots");
                    setHoveredGuidanceIndex(null);
                  }}
                >
                  Screenshot Builder
                </ModePill>
              </ActionGroup>
            }
          >
            <div
              style={{
                marginBottom: 18,
                padding: "0 0 14px",
                borderRadius: 0,
                background: "transparent",
                borderBottom: "1px solid var(--oasys-color-border, rgba(148, 163, 184, 0.18))",
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--color-text, #e8edf7)",
                  marginBottom: 6,
                }}
              >
                {builderTitle}
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "var(--color-text-secondary, #9aa9c2)",
                }}
              >
                {builderSubtitle}
              </div>
            </div>

            {builderMode === "guided" ? (
              <SOPGenerator apiBaseUrl={apiBaseUrl} />
            ) : (
              <ScreenshotFlow apiBaseUrl={apiBaseUrl} />
            )}
          </SurfaceCard>
        </div>

        <div style={{ minWidth: 0 }}>
          <SurfaceCard
            title="Builder Guidance"
            subtitle="Choose the right SOP creation approach and set quality expectations before you start."
          >
            <div style={{ display: "grid", gap: 14 }}>
              {operatorCards.map((item, index) => (
                <InsightCard
                  key={item.title}
                  title={item.title}
                  body={item.body}
                  active={hoveredGuidanceIndex === index}
                  onMouseEnter={() => setHoveredGuidanceIndex(index)}
                  onMouseLeave={() => setHoveredGuidanceIndex(null)}
                />
              ))}
            </div>
          </SurfaceCard>
        </div>
      </div>

      <div id="sop-library-workspace">
        <SurfaceCard
          title={libraryTitle}
          subtitle={librarySubtitle}
          right={
            libraryQueueView !== "all" ? (
              <ModePill active onClick={() => setLibraryQueueView("all")}>
                Clear queue view
              </ModePill>
            ) : (
              <ModePill
                active={false}
                onClick={() => openDocumentControlQueue("pending_review")}
              >
                Open Review Queue
              </ModePill>
            )
          }
        >
          {libraryQueueView === "pending_review" ? (
            <div
              style={{
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-warning, #f59e0b), transparent 92%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-warning, #f59e0b), transparent 76%)",
                color: "var(--color-text, #e8edf7)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <strong>Reviewer queue:</strong> This view is for SOP revisions
              waiting on approval. Open a pending record, review the current
              controlled version and candidate version, then approve or reject
              from Version History.
            </div>
          ) : null}

          {libraryQueueView === "pending_acknowledgment" ? (
            <div
              style={{
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 94%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 78%)",
                color: "var(--color-text-secondary, #9aa9c2)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <strong>Pending acknowledgments:</strong> Showing effective SOPs
              that still need your read-and-understood confirmation. Open a SOP,
              review the controlled version, then acknowledge it from the
              Release & acknowledgments card.
            </div>
          ) : null}

          {libraryQueueView === "overdue" ? (
            <div
              style={{
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-error, #b91c1c), transparent 95%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-error, #b91c1c), transparent 78%)",
                color: "var(--color-text-secondary, #9aa9c2)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <strong>Overdue queue:</strong> Showing SOPs flagged by the backend
              outdated detector. Review the SOP, revise it, or regenerate it to
              bring it back into the current controlled document set.
            </div>
          ) : null}

          {libraryQueueView === "archived" ? (
            <div
              style={{
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 88%)",
                color: "var(--color-text-secondary, #9aa9c2)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <strong>Archive:</strong> Archived SOPs are removed from active
              execution and excluded from overdue detection, but retained for
              controlled-document history and audit traceability.
            </div>
          ) : null}

          {libraryQueueView === "active_findings" ? (
            <div
              style={{
                marginBottom: 16,
                padding: "14px 16px",
                borderRadius: 18,
                background:
                  "color-mix(in srgb, var(--color-text, #e8edf7), transparent 96%)",
                border:
                  "1px solid color-mix(in srgb, var(--color-text, #e8edf7), transparent 88%)",
                color: "var(--color-text-secondary, #9aa9c2)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              SOP-to-finding linkage will become active after SOPs can be mapped
              to controls, findings, and remediation workflows.
            </div>
          ) : null}

          <Library
            key={`sop-library-${libraryQueueView}-${targetSopId || "none"}`}
            apiBaseUrl={apiBaseUrl}
            getAccessToken={getAccessToken}
            userProfile={userProfile}
            initialStatusFilter={libraryStatusFilter}
            sopQueueView={libraryQueueView}
            routeParams={routeParams}
            initialSopId={targetSopId}
            copilotTab={builderMode}
            onDocumentControlChanged={refreshDocumentControlQueue}
          />
        </SurfaceCard>
      </div>

      <DocumentControlModal
        queueType={documentControlModal}
        onClose={closeDocumentControlQueue}
        pendingQueue={pendingQueue}
        overdueQueue={overdueQueue}
        archivedQueue={archivedQueue}
        acknowledgmentQueue={acknowledgmentQueue}
        onOpenLibraryQueue={openLibraryQueue}
      />
      </div>
    </div>
  );
}
